import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiArrowLeft, FiCheckCircle, FiUsers, FiHome, FiDollarSign, FiClock, FiHeart, FiShield, FiTrendingUp, FiAlertTriangle } = FiIcons;

const LifeInsuranceQuestionnaire = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [recommendation, setRecommendation] = useState(null);

  const questions = [
    {
      id: 'age',
      title: 'What is your age?',
      type: 'select',
      options: [
        { value: '18-25', label: '18-25 years old' },
        { value: '26-35', label: '26-35 years old' },
        { value: '36-45', label: '36-45 years old' },
        { value: '46-55', label: '46-55 years old' },
        { value: '56-65', label: '56-65 years old' },
        { value: '66-75', label: '66-75 years old' },
        { value: '76-85', label: '76-85 years old' }
      ]
    },
    {
      id: 'dependents',
      title: 'Do you have financial dependents?',
      subtitle: 'This includes spouse, children, elderly parents, or anyone who relies on your income.',
      type: 'radio',
      options: [
        { value: 'spouse-children', label: 'Yes, spouse and/or children under 18' },
        { value: 'spouse-only', label: 'Yes, spouse only (no children)' },
        { value: 'elderly-parents', label: 'Yes, elderly parents or family members' },
        { value: 'none', label: 'No financial dependents' }
      ]
    },
    {
      id: 'income',
      title: 'What is your annual household income?',
      type: 'select',
      options: [
        { value: 'under-50k', label: 'Under $50,000' },
        { value: '50k-75k', label: '$50,000 - $75,000' },
        { value: '75k-100k', label: '$75,000 - $100,000' },
        { value: '100k-150k', label: '$100,000 - $150,000' },
        { value: '150k-250k', label: '$150,000 - $250,000' },
        { value: 'over-250k', label: 'Over $250,000' }
      ]
    },
    {
      id: 'debts',
      title: 'What major debts do you have?',
      subtitle: 'Select all that apply',
      type: 'checkbox',
      options: [
        { value: 'mortgage', label: 'Mortgage ($200,000+)' },
        { value: 'small-mortgage', label: 'Mortgage (Under $200,000)' },
        { value: 'business-loan', label: 'Business loan' },
        { value: 'student-loan', label: 'Student loans' },
        { value: 'credit-card', label: 'Credit card debt' },
        { value: 'none', label: 'No significant debt' }
      ]
    },
    {
      id: 'timeline',
      title: 'How long do you need life insurance protection?',
      type: 'radio',
      options: [
        { value: '10-years', label: '10 years or less (short-term debt coverage)' },
        { value: '15-20-years', label: '15-20 years (until children are independent)' },
        { value: '20-30-years', label: '20-30 years (until retirement)' },
        { value: 'lifetime', label: 'My entire lifetime (permanent coverage)' }
      ]
    },
    {
      id: 'health',
      title: 'How would you describe your current health?',
      type: 'radio',
      options: [
        { value: 'excellent', label: 'Excellent - No health issues, very active' },
        { value: 'good', label: 'Good - Minor health issues, generally healthy' },
        { value: 'fair', label: 'Fair - Some health conditions, taking medications' },
        { value: 'poor', label: 'Poor - Serious health issues or multiple conditions' }
      ]
    },
    {
      id: 'priority',
      title: 'What is most important to you?',
      type: 'radio',
      options: [
        { value: 'lowest-cost', label: 'Lowest possible premium cost' },
        { value: 'maximum-coverage', label: 'Maximum coverage amount' },
        { value: 'no-exam', label: 'No medical exam or hassle' },
        { value: 'cash-value', label: 'Building cash value/investment' },
        { value: 'guaranteed-approval', label: 'Guaranteed approval regardless of health' }
      ]
    },
    {
      id: 'budget',
      title: 'What monthly premium fits your budget?',
      type: 'select',
      options: [
        { value: 'under-25', label: 'Under $25/month' },
        { value: '25-50', label: '$25-50/month' },
        { value: '50-100', label: '$50-100/month' },
        { value: '100-200', label: '$100-200/month' },
        { value: '200-plus', label: '$200+/month' }
      ]
    }
  ];

  const getRecommendation = (answers) => {
    const age = answers.age;
    const dependents = answers.dependents;
    const income = answers.income;
    const debts = answers.debts || [];
    const timeline = answers.timeline;
    const health = answers.health;
    const priority = answers.priority;
    const budget = answers.budget;

    // Calculate realistic coverage needs
    let recommendedCoverage = 250000; // Base coverage
    
    // Adjust based on income
    if (income === 'over-250k') recommendedCoverage = 2000000;
    else if (income === '150k-250k') recommendedCoverage = 1500000;
    else if (income === '100k-150k') recommendedCoverage = 1000000;
    else if (income === '75k-100k') recommendedCoverage = 750000;
    else if (income === '50k-75k') recommendedCoverage = 500000;
    
    // Adjust for debts
    if (debts.includes('mortgage')) recommendedCoverage += 300000;
    if (debts.includes('business-loan')) recommendedCoverage += 200000;
    
    // Adjust for dependents
    if (dependents === 'spouse-children') recommendedCoverage += 200000;
    else if (dependents === 'spouse-only') recommendedCoverage += 100000;

    // Calculate realistic premium based on age, health, and coverage
    const calculateRealPremium = (coverage, ageGroup, healthStatus, insuranceType) => {
      let basePremium = 0;
      
      if (insuranceType === 'term') {
        // Term life rates per $100k coverage
        const termRates = {
          '18-25': { excellent: 8, good: 12, fair: 18, poor: 35 },
          '26-35': { excellent: 10, good: 15, fair: 25, poor: 45 },
          '36-45': { excellent: 18, good: 28, fair: 45, poor: 75 },
          '46-55': { excellent: 45, good: 65, fair: 95, poor: 150 },
          '56-65': { excellent: 85, good: 125, fair: 185, poor: 275 },
          '66-75': { excellent: 185, good: 265, fair: 385, poor: 550 },
          '76-85': { excellent: 385, good: 485, fair: 650, poor: 850 }
        };
        
        basePremium = termRates[ageGroup]?.[healthStatus] || 50;
        return Math.round((coverage / 100000) * basePremium);
        
      } else if (insuranceType === 'whole-life') {
        // Whole life rates for $25k base coverage
        const wholeLifeRates = {
          '18-25': 65, '26-35': 85, '36-45': 115, '46-55': 155,
          '56-65': 215, '66-75': 285, '76-85': 385
        };
        
        basePremium = wholeLifeRates[ageGroup] || 100;
        return Math.round((coverage / 25000) * basePremium);
        
      } else if (insuranceType === 'final-expense') {
        // Final expense rates for $15k base coverage
        const finalExpenseRates = {
          '50-55': 45, '56-65': 65, '66-75': 95, '76-85': 145
        };
        
        basePremium = finalExpenseRates[ageGroup] || 75;
        return Math.round((coverage / 15000) * basePremium);
      }
      
      return basePremium;
    };

    // Check budget compatibility
    const getBudgetRange = (budgetCategory) => {
      switch (budgetCategory) {
        case 'under-25': return { min: 0, max: 25 };
        case '25-50': return { min: 25, max: 50 };
        case '50-100': return { min: 50, max: 100 };
        case '100-200': return { min: 100, max: 200 };
        case '200-plus': return { min: 200, max: 500 };
        default: return { min: 0, max: 25 };
      }
    };

    const budgetRange = getBudgetRange(budget);

    // Logic for Final Expense Insurance
    if (age === '66-75' || age === '76-85') {
      if (priority === 'guaranteed-approval' || health === 'poor' || health === 'fair') {
        const coverage = Math.min(recommendedCoverage, 30000); // Cap at final expense max
        const estimatedCost = calculateRealPremium(coverage, age, health, 'final-expense');
        const budgetMismatch = estimatedCost > budgetRange.max;
        
        return {
          product: 'Final Expense Insurance',
          icon: FiHeart,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          title: 'Final Expense Insurance',
          subtitle: 'Perfect for your age and situation',
          description: 'Guaranteed acceptance life insurance designed specifically for seniors. No medical exam required, and coverage starts immediately.',
          benefits: [
            'Guaranteed approval for ages 50-85',
            'No medical exam or health questions',
            'Coverage from $1,000 to $30,000',
            'Fixed premiums that never increase',
            'Immediate coverage after first payment'
          ],
          whyRecommended: 'Based on your age and preferences, final expense insurance provides guaranteed coverage to help with end-of-life costs without burdening your family.',
          estimatedCost: `$${estimatedCost}/month`,
          recommendedCoverage: `$${coverage.toLocaleString()}`,
          budgetMismatch: budgetMismatch,
          budgetNote: budgetMismatch ? 
            `This premium ($${estimatedCost}/month) exceeds your stated budget of $${budgetRange.max}/month. Consider a smaller coverage amount or adjusting your budget expectations.` : null
        };
      } else {
        const coverage = Math.min(recommendedCoverage, 100000); // Cap for whole life
        const estimatedCost = calculateRealPremium(coverage, age, health, 'whole-life');
        const budgetMismatch = estimatedCost > budgetRange.max;
        
        return {
          product: 'Whole Life Insurance',
          icon: FiShield,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          title: 'Whole Life Insurance',
          subtitle: 'Lifetime protection with cash value',
          description: 'Permanent life insurance that builds cash value over time. Perfect for seniors who want to leave a legacy and have access to cash during their lifetime.',
          benefits: [
            'Lifetime coverage that never expires',
            'Builds cash value you can borrow against',
            'Fixed premiums that never increase',
            'Tax-advantaged death benefit',
            'No medical exam for most applicants'
          ],
          whyRecommended: 'At your age, whole life insurance provides permanent protection and cash value growth, making it ideal for estate planning and leaving a legacy.',
          estimatedCost: `$${estimatedCost}/month`,
          recommendedCoverage: `$${coverage.toLocaleString()}`,
          budgetMismatch: budgetMismatch,
          budgetNote: budgetMismatch ? 
            `This premium ($${estimatedCost}/month) exceeds your stated budget of $${budgetRange.max}/month. Consider reducing coverage amount or increasing your budget.` : null
        };
      }
    }

    // Logic for No Medical Exam
    if (priority === 'no-exam' || health === 'fair') {
      const coverage = Math.min(recommendedCoverage, 1000000); // Cap at no-exam max
      const estimatedCost = calculateRealPremium(coverage, age, health, 'term');
      const budgetMismatch = estimatedCost > budgetRange.max;
      
      return {
        product: 'No Medical Exam Life Insurance',
        icon: FiCheckCircle,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        title: 'No Medical Exam Life Insurance',
        subtitle: 'Quick and hassle-free coverage',
        description: 'Get up to $1 million in life insurance coverage without medical exams, blood tests, or lengthy applications. Perfect for busy professionals.',
        benefits: [
          'No medical exam required',
          'Coverage up to $1 million',
          'Instant approval for qualifying applicants',
          '5-minute online application',
          'Same-day coverage available'
        ],
        whyRecommended: 'Based on your preference for convenience and your health situation, no medical exam insurance gets you covered quickly without the hassle.',
        estimatedCost: `$${estimatedCost}/month`,
        recommendedCoverage: `$${coverage.toLocaleString()}`,
        budgetMismatch: budgetMismatch,
        budgetNote: budgetMismatch ? 
          `This premium ($${estimatedCost}/month) exceeds your stated budget of $${budgetRange.max}/month. Consider reducing coverage to $${Math.round(coverage * (budgetRange.max / estimatedCost / 100000)) * 100000} or increasing your budget.` : null
      };
    }

    // Logic for Million Dollar Coverage
    if (income === 'over-250k' || income === '150k-250k') {
      if (debts.includes('mortgage') || debts.includes('business-loan')) {
        const coverage = Math.max(recommendedCoverage, 1000000);
        const estimatedCost = calculateRealPremium(coverage, age, health, 'term');
        const budgetMismatch = estimatedCost > budgetRange.max;
        
        return {
          product: 'Million Dollar Life Insurance',
          icon: FiDollarSign,
          color: 'text-purple-600',
          bgColor: 'bg-purple-50',
          borderColor: 'border-purple-200',
          title: 'Million Dollar Life Insurance',
          subtitle: 'High-value protection for high achievers',
          description: 'Substantial life insurance coverage up to $3 million for executives, business owners, and high-income professionals.',
          benefits: [
            'Coverage up to $3 million available',
            'Competitive rates for high-value policies',
            'Estate planning benefits',
            'Business succession planning',
            'Tax-advantaged death benefit'
          ],
          whyRecommended: 'With your high income and significant debts, you need substantial coverage to maintain your family\'s lifestyle and protect your assets.',
          estimatedCost: `$${estimatedCost}/month`,
          recommendedCoverage: `$${coverage.toLocaleString()}`,
          budgetMismatch: budgetMismatch,
          budgetNote: budgetMismatch ? 
            `This premium ($${estimatedCost}/month) exceeds your stated budget of $${budgetRange.max}/month. High-value coverage requires higher premiums, but it's essential for your income level. Consider increasing your budget or reducing coverage.` : null
        };
      }
    }

    // Logic for Whole Life (cash value priority)
    if (priority === 'cash-value' || timeline === 'lifetime') {
      const coverage = Math.min(recommendedCoverage, 100000);
      const estimatedCost = calculateRealPremium(coverage, age, health, 'whole-life');
      const budgetMismatch = estimatedCost > budgetRange.max;
      
      return {
        product: 'Whole Life Insurance',
        icon: FiShield,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        title: 'Whole Life Insurance',
        subtitle: 'Lifetime protection with investment benefits',
        description: 'Permanent life insurance that combines protection with cash value accumulation. Perfect for long-term financial planning.',
        benefits: [
          'Lifetime coverage that never expires',
          'Builds cash value you can access',
          'Fixed premiums that never increase',
          'Tax-advantaged growth',
          'Dividend potential with participating policies'
        ],
        whyRecommended: 'Since you want lifetime coverage and cash value growth, whole life insurance provides both protection and a conservative investment component.',
        estimatedCost: `$${estimatedCost}/month`,
        recommendedCoverage: `$${coverage.toLocaleString()}`,
        budgetMismatch: budgetMismatch,
        budgetNote: budgetMismatch ? 
          `This premium ($${estimatedCost}/month) exceeds your stated budget of $${budgetRange.max}/month. Whole life is more expensive but offers permanent coverage and cash value. Consider term life for more affordable protection.` : null
      };
    }

    // Default to Term Life Insurance
    let termLength = '20-Year';
    if (timeline === '10-years') termLength = '10-Year';
    if (timeline === '20-30-years') termLength = '30-Year';

    const coverage = recommendedCoverage;
    const estimatedCost = calculateRealPremium(coverage, age, health, 'term');
    const budgetMismatch = estimatedCost > budgetRange.max;
    
    // Suggest reduced coverage if over budget
    let alternativeCoverage = null;
    if (budgetMismatch) {
      alternativeCoverage = Math.round((coverage * (budgetRange.max / estimatedCost)) / 50000) * 50000;
    }

    return {
      product: 'Term Life Insurance',
      icon: FiClock,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      title: `${termLength} Term Life Insurance`,
      subtitle: 'Maximum coverage at minimum cost',
      description: 'Affordable life insurance that provides substantial coverage during your highest-need years. Perfect for families and homeowners.',
      benefits: [
        'Lowest premium cost for maximum coverage',
        'Coverage amounts from $100,000 to $3 million',
        'Fixed premiums for the entire term',
        'Convertible to permanent insurance',
        'No medical exam for most applicants'
      ],
      whyRecommended: dependents !== 'none' 
        ? 'With your dependents and financial obligations, term life insurance provides the most coverage at the most affordable price during your peak earning years.'
        : 'Term life insurance offers excellent value and flexibility, giving you substantial protection at an affordable cost.',
      estimatedCost: `$${estimatedCost}/month`,
      recommendedCoverage: `$${coverage.toLocaleString()}`,
      budgetMismatch: budgetMismatch,
      budgetNote: budgetMismatch ? 
        `This premium ($${estimatedCost}/month) exceeds your stated budget of $${budgetRange.max}/month. Consider $${alternativeCoverage?.toLocaleString()} coverage for about $${budgetRange.max}/month, or increase your budget to get adequate protection.` : null,
      alternativeCoverage: alternativeCoverage
    };
  };

  const handleAnswer = (questionId, value) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Generate recommendation
      const rec = getRecommendation(answers);
      setRecommendation(rec);
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setAnswers({});
    setRecommendation(null);
  };

  const isCurrentStepComplete = () => {
    if (currentStep >= questions.length) return true;
    const question = questions[currentStep];
    const answer = answers[question.id];
    
    if (question.type === 'checkbox') {
      return answer && answer.length > 0;
    }
    return answer !== undefined && answer !== '';
  };

  const progress = ((currentStep + 1) / (questions.length + 1)) * 100;

  return (
    <section className="py-16 bg-gradient-to-br from-primary-50 to-blue-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
            Find Your Perfect Life Insurance
          </h2>
          <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
            Answer a few quick questions and we'll recommend the best life insurance product for your unique situation.
          </p>
        </motion.div>

        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
          {/* Progress Bar */}
          <div className="bg-secondary-100 h-2">
            <motion.div
              className="bg-primary-600 h-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          {/* Fixed height container to prevent jumping */}
          <div className="p-8 min-h-[600px] flex flex-col">
            <AnimatePresence mode="wait">
              {currentStep < questions.length ? (
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="flex-1 flex flex-col"
                >
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-secondary-500">
                        Question {currentStep + 1} of {questions.length}
                      </span>
                      <span className="text-sm font-medium text-primary-600">
                        {Math.round(progress)}% Complete
                      </span>
                    </div>
                    <h3 className="text-2xl font-bold text-secondary-900 mb-2">
                      {questions[currentStep].title}
                    </h3>
                    {questions[currentStep].subtitle && (
                      <p className="text-secondary-600">
                        {questions[currentStep].subtitle}
                      </p>
                    )}
                  </div>

                  {/* Content area with consistent height */}
                  <div className="flex-1 mb-8">
                    <div className="space-y-3 max-h-[350px] overflow-y-auto">
                      {questions[currentStep].type === 'radio' && (
                        <div className="space-y-3">
                          {questions[currentStep].options.map((option) => (
                            <label
                              key={option.value}
                              className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                                answers[questions[currentStep].id] === option.value
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-secondary-200 hover:border-secondary-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name={questions[currentStep].id}
                                value={option.value}
                                checked={answers[questions[currentStep].id] === option.value}
                                onChange={(e) => handleAnswer(questions[currentStep].id, e.target.value)}
                                className="sr-only"
                              />
                              <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                                answers[questions[currentStep].id] === option.value
                                  ? 'border-primary-500'
                                  : 'border-secondary-300'
                              }`}>
                                {answers[questions[currentStep].id] === option.value && (
                                  <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />
                                )}
                              </div>
                              <span className="text-secondary-900">{option.label}</span>
                            </label>
                          ))}
                        </div>
                      )}

                      {questions[currentStep].type === 'select' && (
                        <select
                          value={answers[questions[currentStep].id] || ''}
                          onChange={(e) => handleAnswer(questions[currentStep].id, e.target.value)}
                          className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="">Please select an option</option>
                          {questions[currentStep].options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}

                      {questions[currentStep].type === 'checkbox' && (
                        <div className="space-y-3">
                          {questions[currentStep].options.map((option) => (
                            <label
                              key={option.value}
                              className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                                answers[questions[currentStep].id]?.includes(option.value)
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-secondary-200 hover:border-secondary-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                value={option.value}
                                checked={answers[questions[currentStep].id]?.includes(option.value) || false}
                                onChange={(e) => {
                                  const currentAnswers = answers[questions[currentStep].id] || [];
                                  if (e.target.checked) {
                                    // If selecting "none", clear all other selections
                                    if (option.value === 'none') {
                                      handleAnswer(questions[currentStep].id, ['none']);
                                    } else {
                                      // Remove "none" if selecting other options
                                      const newAnswers = currentAnswers.filter(a => a !== 'none');
                                      handleAnswer(questions[currentStep].id, [...newAnswers, option.value]);
                                    }
                                  } else {
                                    const newAnswers = currentAnswers.filter(a => a !== option.value);
                                    handleAnswer(questions[currentStep].id, newAnswers);
                                  }
                                }}
                                className="sr-only"
                              />
                              <div className={`w-5 h-5 border-2 rounded mr-3 flex items-center justify-center ${
                                answers[questions[currentStep].id]?.includes(option.value)
                                  ? 'border-primary-500 bg-primary-500'
                                  : 'border-secondary-300'
                              }`}>
                                {answers[questions[currentStep].id]?.includes(option.value) && (
                                  <SafeIcon icon={FiCheckCircle} className="w-3 h-3 text-white" />
                                )}
                              </div>
                              <span className="text-secondary-900">{option.label}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Fixed position buttons */}
                  <div className="flex justify-between">
                    <button
                      onClick={handlePrevious}
                      disabled={currentStep === 0}
                      className="inline-flex items-center px-6 py-3 bg-secondary-100 text-secondary-700 font-semibold rounded-lg hover:bg-secondary-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <SafeIcon icon={FiArrowLeft} className="h-5 w-5 mr-2" />
                      Previous
                    </button>
                    <button
                      onClick={handleNext}
                      disabled={!isCurrentStepComplete()}
                      className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {currentStep === questions.length - 1 ? 'Get Recommendation' : 'Next'}
                      <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="recommendation"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="text-center"
                >
                  <div className={`${recommendation.bgColor} ${recommendation.borderColor} border-2 rounded-xl p-8 mb-8`}>
                    <div className="flex items-center justify-center mb-6">
                      <div className={`${recommendation.bgColor} rounded-full p-4 w-20 h-20 flex items-center justify-center border ${recommendation.borderColor}`}>
                        <SafeIcon icon={recommendation.icon} className={`h-10 w-10 ${recommendation.color}`} />
                      </div>
                    </div>
                    
                    <h3 className="text-3xl font-bold text-secondary-900 mb-2">
                      {recommendation.title}
                    </h3>
                    <p className={`text-lg font-medium ${recommendation.color} mb-4`}>
                      {recommendation.subtitle}
                    </p>
                    <p className="text-secondary-700 mb-6 max-w-2xl mx-auto">
                      {recommendation.description}
                    </p>

                    <div className="bg-white rounded-lg p-6 mb-6">
                      <h4 className="font-bold text-secondary-900 mb-4">Key Benefits:</h4>
                      <ul className="space-y-2 text-left max-w-md mx-auto">
                        {recommendation.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start">
                            <SafeIcon icon={FiCheckCircle} className={`h-5 w-5 ${recommendation.color} mr-2 mt-0.5`} />
                            <span className="text-secondary-700 text-sm">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-secondary-50 rounded-lg p-4 mb-6">
                      <h4 className="font-semibold text-secondary-900 mb-2">Why We Recommend This:</h4>
                      <p className="text-sm text-secondary-700">{recommendation.whyRecommended}</p>
                    </div>

                    <div className="mb-6">
                      <p className="text-lg font-semibold text-secondary-900 mb-2">
                        Recommended Coverage: <span className={recommendation.color}>{recommendation.recommendedCoverage}</span>
                      </p>
                      <p className="text-lg font-semibold text-secondary-900">
                        Estimated Cost: <span className={recommendation.color}>{recommendation.estimatedCost}</span>
                      </p>
                      <p className="text-sm text-secondary-500 mt-1">
                        *Estimate based on your answers. Actual rates may vary.
                      </p>
                    </div>

                    {/* Budget Mismatch Warning */}
                    {recommendation.budgetMismatch && (
                      <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 mb-6">
                        <div className="flex items-start">
                          <SafeIcon icon={FiAlertTriangle} className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                          <div className="text-left">
                            <h5 className="font-semibold text-yellow-800 mb-1">Budget Consideration</h5>
                            <p className="text-sm text-yellow-700">{recommendation.budgetNote}</p>
                            {recommendation.alternativeCoverage && (
                              <p className="text-sm text-yellow-700 mt-2">
                                <strong>Alternative:</strong> Consider ${recommendation.alternativeCoverage.toLocaleString()} coverage to fit your budget.
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a
                      href="https://quote.burbankinsuranceservices.com/Dt2tCn"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-8 py-4 bg-primary-600 text-white font-bold rounded-lg hover:bg-primary-700 transition-colors shadow-lg"
                    >
                      Get My {recommendation.product} Quote
                      <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
                    </a>
                    <button
                      onClick={handleRestart}
                      className="inline-flex items-center justify-center px-8 py-3 bg-secondary-100 text-secondary-700 font-semibold rounded-lg hover:bg-secondary-200 transition-colors"
                    >
                      Start Over
                    </button>
                  </div>

                  <p className="text-sm text-secondary-500 mt-4">
                    Application takes less than 5 minutes • Licensed in California
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LifeInsuranceQuestionnaire;